
import { useState, useEffect } from "react";
import { FilmInventory, FilmFormValues, mapStatusToDatabase, mapStatusFromDatabase } from "@/types/inventory";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function useFilmInventory() {
  const [films, setFilms] = useState<FilmInventory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSort, setActiveSort] = useState<string | null>("brand");
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const fetchFilms = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let query = supabase
        .from('film_inventory')
        .select('*');
        
      if (activeSort && sortDirection) {
        query = query.order(activeSort, { ascending: sortDirection === 'asc' });
      } else {
        query = query.order('created_at', { ascending: false });
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      if (data) {
        // Map database statuses to UI statuses and ensure date formats are consistent
        const mappedData = data.map(film => {
          // Check if film is expired based on expiration date
          let status = mapStatusFromDatabase(film.status as any);
          
          // If the film has an expiration date and it's in the past, mark as expired
          // Only override if it's currently marked as 'fresh'
          if (film.status === 'fresh' && film.expiration_date) {
            const expirationDate = new Date(film.expiration_date);
            const today = new Date();
            if (expirationDate < today) {
              status = 'expired';
            }
          }
          
          return {
            ...film,
            status,
            // Make sure dates are properly handled as strings
            expiration_date: film.expiration_date ? film.expiration_date : null
          };
        });
        
        setFilms(mappedData as unknown as FilmInventory[]);
      } else {
        setFilms([]);
      }
    } catch (err: any) {
      console.error('Error fetching films:', err);
      setError(err.message || 'Failed to load films');
    } finally {
      setLoading(false);
    }
  };

  const handleAddFilm = async (values: FilmFormValues) => {
    try {
      const user = supabase.auth.getUser();
      const userId = (await user).data.user?.id;
      
      if (!userId) {
        toast.error('You must be signed in to add film');
        return false;
      }
      
      // Format the expiration date to a proper YYYY-MM-DD format that Postgres can accept
      let formattedExpirationDate: string | null = null;
      if (values.expiration_date) {
        const [year, month] = values.expiration_date.split('-');
        // Get the last day of the month for a valid date
        const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
        formattedExpirationDate = `${year}-${month}-${lastDay.toString().padStart(2, '0')}`;
      }
      
      // Map UI status to database status
      const databaseStatus = mapStatusToDatabase(values.status);
      
      // Use type assertion to bypass TypeScript's strict checking
      const { error } = await supabase
        .from('film_inventory')
        .insert({
          film_name: values.film_name,
          film_type: values.film_type,
          status: databaseStatus,
          expiration_date: formattedExpirationDate,
          iso: values.iso,
          brand: values.brand,
          quantity: values.quantity,
          notes: values.notes || null,
          user_id: userId,
        } as any);

      if (error) {
        console.error('Insert error:', error);
        throw error;
      }
      
      toast.success('Film added to inventory');
      fetchFilms();
      return true;
    } catch (error: any) {
      toast.error(`Error adding film: ${error.message}`);
      console.error('Error adding film:', error);
      return false;
    }
  };

  const handleSortChange = (value: string) => {
    setActiveSort(value);
    if (!sortDirection) {
      setSortDirection('asc');
    }
  };

  const handleDirectionChange = () => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
  };

  useEffect(() => {
    fetchFilms();
  }, [activeSort, sortDirection]);

  return {
    films,
    loading,
    error,
    activeSort,
    sortDirection,
    fetchFilms,
    handleAddFilm,
    handleSortChange,
    handleDirectionChange
  };
}
